import React, { Dispatch, useEffect, useReducer, useRef, useState } from "react";

import { ComboBox, ComboBoxCloseEvent, ComboBoxFilterChangeEvent, DropDownListChangeEvent, MultiSelectChangeEvent, MultiSelectFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Input, InputChangeEvent, NumericTextBox, NumericTextBoxChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import ApiCommunicator, { useApiService } from "@selas/api-communication";
import { IAction, IEntity } from "@selas/models";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, ManageableField, ManageableMultiSelect, SearchBox, setEntity, TextArea, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import cloneDeep from "lodash/cloneDeep";
import find from "lodash/find";
import map from "lodash/map";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { hasPermission } from "../../../services/authentication";
import { catalogueServiceReducer, invoicingMomentReducer, languageReducer, opportunityServiceReducer, serviceTypeReducer } from "../../../state/reducers";
import { Permission } from "../../../utils/enums";
import { ICatalogueService, IInvoicingMoment, ILanguage, IOpportunityService, IServiceLanguage, IServiceType } from "../../../utils/types/models";
import CatalogueServiceEditor from "../catalogue/catalogueService";
import ServiceTypeEditor from "../catalogue/serviceType";
import InvoicingMomentEditor from "../masterdata/invoicingMoment";
import LanguageEditor from "../personInfo/language";

interface IProps {
	opportunityId: number;
}

const OpportunityServiceEditor: React.FC<(IAddEntityScreenProps<IOpportunityService> | IEditEntityScreenProps<IOpportunityService>) & IProps> = (
	props: (IAddEntityScreenProps<IOpportunityService> | IEditEntityScreenProps<IOpportunityService>) & IProps
) => {
	const { t } = useTranslation();

	const initialOpportunityService: IOpportunityService = {
		id: (props as IEditEntityScreenProps<IOpportunityService>).recordId || 0,
		opportunityId: props.opportunityId,
		code: "",
		typeId: null,
		languages: [],
		collectiveHours: 0,
		individualHours: 0,
		numberOfMonths: 0,
		invoicingMomentId: null,
		salesPrice: 0,
		account: undefined,
		amount: 1,
		isProjectLevelService: false
	};
	const [opportunityService, setOpportunityService] = useState<IOpportunityService>(initialOpportunityService);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const [catalogueService, setCatalogueService] = useState<ICatalogueService>();
	const [opportunityServiceState, opportunityServiceDispatch] = useReducer(opportunityServiceReducer, getInitialState<IOpportunityService>());
	const [catalogueServiceState, catalogueServiceDispatch] = useReducer(catalogueServiceReducer, getInitialState<ICatalogueService>());
	const [serviceTypeState, serviceTypeDispatch] = useReducer(serviceTypeReducer, getInitialState<IServiceType>());
	const [languageState, languageDispatch] = useReducer(languageReducer, getInitialState<ILanguage>());
	const [invoicingMomentState, invoicingMomentDispatch] = useReducer(invoicingMomentReducer, getInitialState<IInvoicingMoment>());
	const firstField: React.MutableRefObject<Input | ComboBox> = useRef();
	const apiService: ApiCommunicator = useApiService();

	useEffect(() => {
		if (opportunityServiceState.entity) {
			setOpportunityService(opportunityServiceState.entity);
		}
	}, [opportunityServiceState.entity]);

	useEffect(() => {
		if (hasPermission(Permission.OpportunitiesUpdate)) {
			apiService.callApi(languageDispatch, Endpoint.Languages, "GET");
		}
	}, [apiService]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent | ComboBoxCloseEvent | DropDownListChangeEvent | React.ChangeEvent<HTMLTextAreaElement> | NumericTextBoxChangeEvent): void {
		setOpportunityService(handleChange(opportunityService, event));
		setDataChanged(true);
	}

	function onCatalogueServiceChange(newCatalogueService: ICatalogueService): void {
		setCatalogueService(newCatalogueService);
		if (newCatalogueService) {
			const newOpportunityService: IOpportunityService = {
				...newCatalogueService,
				id: opportunityService.id,
				opportunityId: opportunityService.opportunityId,
				amount: opportunityService.amount,
				languages: map(newCatalogueService.languages, (language: IServiceLanguage<ICatalogueService>) => ({
					serviceId: opportunityService.id,
					languageId: language.languageId,
					language: language.language
				}))
			};
			updateOpportunityService(newOpportunityService);
		}
	}

	function setChild<T extends IEntity>(entity: T, field: keyof IOpportunityService, idField: keyof IOpportunityService): void {
		const newOpportunityService: IOpportunityService = setEntity(opportunityService, entity, field, idField);
		updateOpportunityService(newOpportunityService);
	}

	function onMultiSelectChange(event: MultiSelectChangeEvent): void {
		const newOpportunityService: IOpportunityService = cloneDeep(opportunityService);

		if (event.value) {
			if (event.target.name === "languages") {
				newOpportunityService.languages = [];
				for (const lang of event.value) {
					newOpportunityService.languages.push({ serviceId: newOpportunityService.id, languageId: lang.id, language: lang });
				}
			}
			updateOpportunityService(newOpportunityService);
		}
	}

	function addOrUpdateLanguage(language: ILanguage): void {
		const newOpportunityService: IOpportunityService = cloneDeep(opportunityService);
		const existingServiceLanguage: IServiceLanguage<IOpportunityService> = find(newOpportunityService.languages, { languageId: language.id });
		if (existingServiceLanguage) {
			existingServiceLanguage.language = language;
		} else {
			newOpportunityService.languages.push({
				serviceId: newOpportunityService.id,
				languageId: language.id,
				language
			});
		}
		apiService.callApi(languageDispatch, Endpoint.Languages, "GET");
		updateOpportunityService(newOpportunityService);
	}

	function updateOpportunityService(newOpportunityService: IOpportunityService): void {
		setOpportunityService(newOpportunityService);
		setDataChanged(true);
	}

	function onFilterChange(event: ComboBoxFilterChangeEvent | MultiSelectFilterChangeEvent): void {
		let dispatch: Dispatch<IAction>;
		let filterEndpoint: Endpoint;
		switch (event.target.name) {
			case "typeId":
				dispatch = serviceTypeDispatch;
				filterEndpoint = Endpoint.ServiceTypes;
				break;
			case "invoicingMomentId":
				dispatch = invoicingMomentDispatch;
				filterEndpoint = Endpoint.InvoicingMoments;
				break;
			case "catalogueService":
				dispatch = catalogueServiceDispatch;
				filterEndpoint = Endpoint.CatalogueServices;
				break;
			case "languages":
				dispatch = languageDispatch;
				filterEndpoint = Endpoint.Languages;
				break;
		}
		apiService.callApi(dispatch, filterEndpoint, "GET", { search: event.filter.value });
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(opportunityService.code)) {
			messages.push(t("fill_in_required_field", { field: t("serviceCode").toLowerCase() }));
		}
		if (!opportunityService.typeId) {
			messages.push(t("fill_in_required_field", { field: t("serviceType").toLowerCase() }));
		}
		if (!opportunityService.invoicingMomentId) {
			messages.push(t("fill_in_required_field", { field: t("invoicingMoment").toLowerCase() }));
		}
		if (!opportunityService.languages || opportunityService.languages.length <= 0) {
			messages.push(t("fill_in_required_field", { field: t("languages").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IOpportunityService): void {
		if (record && !close) {
			setOpportunityService(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IOpportunityService>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={opportunityService}
			endpoint={Endpoint.OpportunityServices}
			extraUrlParams={{ opportunityId: props.opportunityId }}
			entityState={opportunityServiceState}
			entityType={t("opportunityService")}
			dispatch={opportunityServiceDispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={opportunityService.code}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				{!opportunityService.id && (
					<ManageableField
						fieldLabel={t("service")}
						recordId={catalogueService?.id}
						addScreen={{ screen: CatalogueServiceEditor, isAllowed: hasPermission(Permission.CatalogueServicesAdd) }}
						editScreen={{ screen: CatalogueServiceEditor, isAllowed: hasPermission(Permission.CatalogueServicesUpdate) }}
						setEntity={(service: ICatalogueService) => onCatalogueServiceChange(service)}
						readOnly={readonly}
					>
						<SearchBox
							name="catalogueService"
							entities={catalogueServiceState.entities}
							isLoading={catalogueServiceState.areEntitiesLoading}
							entityId={catalogueService?.id}
							entity={catalogueService}
							textField="code"
							onFilterChange={onFilterChange}
							onClose={(event: ComboBoxCloseEvent) => onCatalogueServiceChange(event.target.value)}
							onClear={() => setCatalogueService(undefined)}
							disabled={readonly}
							myRef={firstField as React.RefObject<ComboBox>}
						/>
					</ManageableField>
				)}
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("serviceCode")} *</span>
							<Input
								name="code"
								ref={opportunityService.id ? (firstField as React.RefObject<Input>) : undefined}
								value={opportunityService.code}
								onChange={onChange}
								required
								disabled={readonly}
							/>
						</label>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<ManageableField
							fieldLabel={`${t("serviceType")} *`}
							recordId={opportunityService.typeId}
							addScreen={{ screen: ServiceTypeEditor, isAllowed: hasPermission(Permission.ServiceTypesAdd) }}
							editScreen={{ screen: ServiceTypeEditor, isAllowed: hasPermission(Permission.ServiceTypesUpdate) }}
							setEntity={(type: IServiceType) => setChild(type, "type", "typeId")}
							readOnly={readonly}
						>
							<SearchBox
								name="typeId"
								entities={serviceTypeState.entities}
								isLoading={serviceTypeState.areEntitiesLoading}
								entityId={opportunityService.typeId}
								entity={opportunityService.type}
								required
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "type", "typeId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
					<div className="col">
						<ManageableMultiSelect
							fieldLabel={`${t("languages")} *`}
							addScreen={{ screen: LanguageEditor, isAllowed: hasPermission(Permission.LanguagesAdd) }}
							editScreen={{ screen: LanguageEditor, isAllowed: hasPermission(Permission.LanguagesUpdate) }}
							setEntity={addOrUpdateLanguage}
							name="languages"
							data={languageState.entities}
							textField="name"
							dataItemKey="id"
							value={map(opportunityService.languages, "language")}
							loading={languageState.areEntitiesLoading}
							onChange={onMultiSelectChange}
							onFilterChange={onFilterChange}
							filterable
							autoClose={false}
							disabled={readonly}
							readOnly={readonly}
							required
						/>
					</div>
				</div>
				<label className="k-form-field">
					<span>{t("shortDescription")}</span>
					<TextArea name="shortDescription" value={opportunityService.shortDescription} onChange={onChange} disabled={readonly} />
				</label>
				<label className="k-form-field">
					<span>{t("longDescription")}</span>
					<TextArea name="longDescription" value={opportunityService.longDescription} onChange={onChange} disabled={readonly} />
				</label>
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("collectiveHours")}</span>
							<NumericTextBox name="collectiveHours" value={opportunityService.collectiveHours} onChange={onChange} format="n2" min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("individualHours")}</span>
							<NumericTextBox name="individualHours" value={opportunityService.individualHours} onChange={onChange} format="n2" min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("numberOfMonths")}</span>
							<NumericTextBox name="numberOfMonths" value={opportunityService.numberOfMonths} onChange={onChange} format="n0" min={0} disabled={readonly} />
						</label>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<ManageableField
							fieldLabel={`${t("invoicingMoment")} *`}
							recordId={opportunityService.invoicingMomentId}
							addScreen={{ screen: InvoicingMomentEditor, isAllowed: hasPermission(Permission.InvoicingMomentsAdd) }}
							editScreen={{ screen: InvoicingMomentEditor, isAllowed: hasPermission(Permission.InvoicingMomentsUpdate) }}
							setEntity={(invoicingMoment: IInvoicingMoment) => setChild(invoicingMoment, "invoicingMoment", "invoicingMomentId")}
							readOnly={readonly}
						>
							<SearchBox
								name="invoicingMomentId"
								entities={invoicingMomentState.entities}
								isLoading={invoicingMomentState.areEntitiesLoading}
								entityId={opportunityService.invoicingMomentId}
								entity={opportunityService.invoicingMoment}
								required
								textField="name"
								onFilterChange={onFilterChange}
								onClose={onChange}
								onClear={() => setChild(null, "invoicingMoment", "invoicingMomentId")}
								disabled={readonly}
							/>
						</ManageableField>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("account")}</span>
							<Input name="account" value={opportunityService.account} onChange={onChange} disabled={readonly} />
						</label>
					</div>
				</div>
				<div className="row">
					<div className="col">
						<label className="k-form-field">
							<span>{t("salesPrice")}</span>
							<NumericTextBox name="salesPrice" value={opportunityService.salesPrice} onChange={onChange} format="n2" disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("amount")}</span>
							<NumericTextBox name="amount" value={opportunityService.amount} onChange={onChange} min={0} disabled={readonly} />
						</label>
					</div>
					<div className="col">
						<label className="k-form-field">
							<span>{t("subtotal")}</span>
							<NumericTextBox name="subtotal" value={opportunityService.amount * opportunityService.salesPrice} min={0} format="n2" disabled />
						</label>
					</div>
				</div>
				<div className="k-form-field">
					<div>{t("isProjectLevelService")}</div>
					<YesNoSwitch name="isProjectLevelService" checked={opportunityService.isProjectLevelService} onChange={onChange} disabled={readonly} />
				</div>
			</div>
		</EntityEditor>
	);
};

export default OpportunityServiceEditor;
